function applyStickyTransforms(container) {
  if (!container) return

  const { scrollLeft, scrollTop } = container
  const frozenColumns = container.querySelectorAll('.sticky')
  const stickyHeader = container.dataset.stickyHeader === 'true'

  frozenColumns.forEach((column) => {
    const isHeader = column.closest('thead')
    if (isHeader && stickyHeader) {
      column.style.transform = `translate(${scrollLeft}px, ${scrollTop}px)`
    } else {
      column.style.transform = `translateX(${scrollLeft}px)`
    }
  })

  // Apply vertical sticky to all non-sticky header cells (only if sticky header is enabled)
  if (stickyHeader) {
    const headerCells = container.querySelectorAll('thead th')
    headerCells.forEach((cell) => {
      if (!cell.classList.contains('sticky')) {
        cell.style.transform = `translateY(${scrollTop}px)`
      }
    })
  }
}

function truncate(text, limit = 30) {
  if (typeof text !== 'string') return ''
  return text.length <= limit ? text : `${text.slice(0, limit - 3)}...`
}

// The template key of a lazy cell (see materializeLazyCell), or undefined for
// an eager cell.
function lazyCellKey(td) {
  return td?.dataset?.lazyCellTemplate ?? td?.dataset?.lazyCellTraitId
}

// Whether a lazy cell can be edited in place: it already has its editor, or
// the page has its template. Rows a reload brought in after someone changed a
// column can name a template the page lacks; keyboard navigation and paste
// pass over those cells.
function lazyCellEditable(td) {
  const key = lazyCellKey(td)
  if (key === undefined) return false
  if (td.querySelector('[data-controller~="inline-editable-form"]')) return true
  return document.querySelector(`template[data-lazy-cell-form="${key}"]`) !== null
}

// Swap a lazy cell (a value-only <td>) for the real inline editor, cloned
// from a <template data-lazy-cell-form>. Two grids use it:
//   - account samples (account_samples/_cell_trait): one template per trait
//     column (data-lazy-cell-trait-id). The cell gives the form's URL and
//     method, and the editor brings its own .value, which takes a copy of the
//     cell's display.
//   - task Results (jobs/v2_sample_result_field_values/_inline_form): one
//     template per editor (data-lazy-cell-template). The cell's record id
//     replaces __ID__ in the form action and Expand link, and the cell's own
//     .value moves into the editor, keeping its status colours and tooltip.
// Must stay synchronous: inline-editable-form's keyboard navigation
// materializes the target cell mid-keydown and focuses its field in the same
// task. Returns true when the cell is (or already was) editable in place,
// false when the page has no template for it.
function materializeLazyCell(td) {
  const key = lazyCellKey(td)
  if (key === undefined) {
    // Not a lazy cell (eager cell or non-trait column) — nothing to do.
    return true
  }
  if (td.querySelector('[data-controller~="inline-editable-form"]')) return true // already materialized

  const template = document.querySelector(`template[data-lazy-cell-form="${key}"]`)
  if (!template) return false

  const fragment = template.content.cloneNode(true)
  const wrapper = fragment.firstElementChild

  const form = wrapper.querySelector('form')
  if (!form) return false
  if (td.dataset.lazyCellUrl) form.setAttribute('action', td.dataset.lazyCellUrl)
  if (td.dataset.lazyCellMethod === 'patch') {
    const methodInput = document.createElement('input')
    methodInput.type = 'hidden'
    methodInput.name = '_method'
    methodInput.value = 'patch'
    form.appendChild(methodInput)
  }
  const recordId = td.dataset.lazyCellId
  if (recordId) {
    wrapper.querySelectorAll('form[action], a[href]').forEach((el) => {
      const attr = el.tagName === 'FORM' ? 'action' : 'href'
      el.setAttribute(attr, el.getAttribute(attr).replace('__ID__', recordId))
    })
  }

  const field = wrapper.querySelector('[data-target~="inline-editable-form.field"]')
  if (field) {
    field.value = td.dataset.lazyCellValue || ''
    // A saved value that isn't one of the select's options selects nothing;
    // show the first option, as a server-rendered select would.
    if (field.tagName === 'SELECT' && field.selectedIndex === -1) field.selectedIndex = 0
    if (field.tagName === 'INPUT') field.setAttribute('value', field.value)
    // Seed originalValue before inline-editable-form connects, mirroring what
    // setOriginalValue would read from a server-rendered form.
    $(field).data('originalValue', field.value)
  }

  const unitSelect = wrapper.querySelector('select[name="sample_trait[unit]"]')
  if (unitSelect && td.dataset.lazyCellUnit) unitSelect.value = td.dataset.lazyCellUnit

  const display = td.querySelector('.value')
  const editorValue = wrapper.querySelector('.value')
  if (display && editorValue) {
    // Carry the rendered display (value + unit label) into the editor's .value.
    editorValue.innerHTML = display.innerHTML
  } else if (display) {
    display.dataset.action = 'click->inline-editable-form#showForm'
    wrapper.insertBefore(display, wrapper.firstChild)
  }

  td.innerHTML = ''
  td.appendChild(wrapper)
  return true
}

export { truncate, applyStickyTransforms, materializeLazyCell, lazyCellEditable }
